import { useCallback, useEffect, useMemo, useState } from 'react'
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline'
import type { AxiosError } from 'axios'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SettingsIntegrationSection from '@/components/SettingsIntegrationSection'
import { toast } from '@/store/toastStore'
import {
  getExternalCredentials,
  putExternalCredentials,
  type ExternalCredentialResponse,
} from '@/services/accounting'
import { getSettings } from '@/services/settings'
import { testCloverApiCredentials } from '@/services/tips'

type CloverTokenRow = { merchantId: string; store: string; token: string }

function parseJsonSetting<T>(raw: string | undefined, fallback: T, label: string): T {
  if (!raw?.trim()) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    toast.warning(`Could not parse ${label}; showing an empty table.`)
    return fallback
  }
}

function merchantRowsFromRecord(record: Record<string, string>): Array<{
  merchantId: string
  store: string
}> {
  return Object.entries(record).map(([merchantId, store]) => ({ merchantId, store }))
}

function formatTimestamp(value?: string | null): string | null {
  if (!value) return null
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return value
  }
}

function cloverTestErrorMessage(err: unknown): string {
  const axiosErr = err as AxiosError<{ message?: string }>
  return axiosErr.response?.data?.message ?? 'Connection failed'
}

export default function CloverIntegrationSection() {
  const [loading, setLoading] = useState(true)
  const [merchants, setMerchants] = useState<CloverTokenRow[]>([])
  const [credentialStatus, setCredentialStatus] = useState<ExternalCredentialResponse | null>(
    null
  )
  const [saving, setSaving] = useState(false)
  const [inputKey, setInputKey] = useState(0)
  const [testingMerchantId, setTestingMerchantId] = useState<string | null>(null)
  const [testingAll, setTestingAll] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const [settings, creds] = await Promise.all([
        getSettings(),
        getExternalCredentials('clover_api'),
      ])
      const record = parseJsonSetting<Record<string, string>>(
        settings.settings.clover_merchants?.value,
        {},
        'Clover merchants'
      )
      setMerchants(
        merchantRowsFromRecord(record).map((row) => ({
          ...row,
          token: '',
        }))
      )
      setCredentialStatus(creds)
    } catch (err) {
      toast.error('Failed to load Clover API tokens')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = useMemo(() => merchants.some((row) => row.token.trim() !== ''), [merchants])

  async function persistTokens(): Promise<boolean> {
    const merchantTokens: Record<string, string> = {}
    for (const row of merchants) {
      const token = row.token.trim()
      if (token) {
        merchantTokens[row.merchantId] = token
      }
    }
    if (Object.keys(merchantTokens).length === 0) {
      toast.warning('Enter at least one API token to save')
      return false
    }
    try {
      setSaving(true)
      await putExternalCredentials('clover_api', { merchant_tokens: merchantTokens })
      const creds = await getExternalCredentials('clover_api')
      setCredentialStatus(creds)
      setMerchants((prev) => prev.map((row) => ({ ...row, token: '' })))
      setInputKey((k) => k + 1)
      toast.success('Clover API tokens saved')
      return true
    } catch (err) {
      toast.error('Failed to save Clover API tokens')
      console.error(err)
      return false
    } finally {
      setSaving(false)
    }
  }

  async function handleTest(merchantId?: string) {
    try {
      if (dirty) {
        const saved = await persistTokens()
        if (!saved) return
      }
      if (merchantId) {
        setTestingMerchantId(merchantId)
      } else {
        setTestingAll(true)
      }
      await testCloverApiCredentials(merchantId)
      toast.success('Connection OK')
      const creds = await getExternalCredentials('clover_api')
      setCredentialStatus(creds)
    } catch (err) {
      toast.error(cloverTestErrorMessage(err))
      console.error(err)
    } finally {
      if (merchantId) {
        setTestingMerchantId((prev) => (prev === merchantId ? null : prev))
      } else {
        setTestingAll(false)
      }
    }
  }

  const configured = credentialStatus?.configured ?? false
  const updatedAt = formatTimestamp(credentialStatus?.updated_at)
  const verifiedAt = formatTimestamp(credentialStatus?.last_verified_at)

  const icon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100/90 ring-1 ring-emerald-200/50">
      <BuildingStorefrontIcon className="h-5 w-5 text-emerald-700" />
    </div>
  )

  return (
    <SettingsIntegrationSection
      title="Clover integration"
      icon={icon}
      detail={
        <>
          <p>
            Official Clover v3 bearer tokens used by Tip Calculator. These are not the Clover Daily
            Reports session tokens.
          </p>
          <p>
            Add merchant IDs in Payroll → Payroll Settings → Clover merchants. After you save, token
            fields clear on purpose — secrets are not sent back to the browser. Leave a field blank
            to keep the stored value. Other users on this Bookkeeper database share these tokens.
          </p>
        </>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className={configured ? 'font-medium text-emerald-700' : 'font-medium text-amber-700'}>
              {configured ? 'Configured' : 'Not configured'}
            </span>
            {updatedAt ? <span>Last updated {updatedAt}</span> : null}
            {verifiedAt ? <span>Last verified {verifiedAt}</span> : null}
            {dirty ? <span className="text-amber-600">Unsaved</span> : null}
          </div>
          {merchants.length === 0 ? (
            <p className="text-sm text-gray-500">
              Add merchant IDs in Payroll → Payroll Settings → Clover merchants, then return here to
              paste API tokens.
            </p>
          ) : (
            <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
              <table className="table-forecast min-w-full">
                <thead>
                  <tr>
                    <th>Store</th>
                    <th>Merchant ID</th>
                    <th>API token</th>
                    <th className="w-24" />
                  </tr>
                </thead>
                <tbody>
                  {merchants.map((row) => (
                    <tr key={row.merchantId}>
                      <td className="text-sm text-gray-900">{row.store}</td>
                      <td className="font-mono text-sm text-gray-700">{row.merchantId}</td>
                      <td>
                        <input
                          key={`${row.merchantId}-${inputKey}`}
                          type="password"
                          className="input w-full min-w-[10rem] text-sm"
                          value={row.token}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          onChange={(e) =>
                            setMerchants((prev) =>
                              prev.map((r) =>
                                r.merchantId === row.merchantId
                                  ? { ...r, token: e.target.value }
                                  : r
                              )
                            )
                          }
                        />
                      </td>
                      <td>
                        <Button
                          variant="outline"
                          disabled={testingMerchantId === row.merchantId || testingAll}
                          onClick={() => void handleTest(row.merchantId)}
                        >
                          {testingMerchantId === row.merchantId ? 'Testing…' : 'Test'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {merchants.length > 0 ? (
              <Button
                variant="outline"
                disabled={testingAll || testingMerchantId !== null}
                onClick={() => void handleTest()}
              >
                {testingAll ? 'Testing…' : 'Test all'}
              </Button>
            ) : null}
            <Button gradient onClick={() => void persistTokens()} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </SettingsIntegrationSection>
  )
}
